import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { OrderType, RefundableType } from 'propTypes';
import ModalComponent from 'components/ModalComponent';
import Placeholder from 'components/Placeholder';
import { orders, refunds } from 'ducks';
import { formatCurrency, parseCurrency } from 'utils';
import registeredComponent from 'utils/registeredComponent';
import { dependentDuck, duckify, singleDuck } from 'utils/duckify';
import FontAwesome from 'utils/FA';
import { faExternalLink } from '@fortawesome/pro-solid-svg-icons/faExternalLink';

import './styles/refunds.scss';

const { I18n = {} } = global;
const DecimalFormatter = new Intl.NumberFormat(I18n.locale || 'en-US', { minimumFractionDigits: 2 });

const PartialRefund = ({ newRefund, updateAmount }) => (
  <label htmlFor='partial-amount-default' className='refund-partial-amount'>
    <span className='amount-label'>Amount</span>
    &nbsp;
    <input
      id='partial-amount-default'
      type='text'
      onChange={(e) => updateAmount(e.currentTarget.value.trim())}
    />
    &nbsp;
    <span className='amount-help help-text'>
      {`(max. ${DecimalFormatter.format(newRefund.refundable_amount)})`}
    </span>
  </label>
);

PartialRefund.propTypes = {
  newRefund: RefundableType.isRequired,
  updateAmount: PropTypes.func.isRequired
};

const CategorizedPartialRefund = ({ newRefund, updateAmount }) => (
  <React.Fragment>
    {Object.keys(newRefund.refundable_categories).map(id => (
      <React.Fragment key={id}>
        <label htmlFor={`partial-amount-${id}`} className='refund-partial-amount'>
          <span className='amount-label'>{newRefund.refundable_categories[id].name}</span>
          &nbsp;
          <input
            id={`partial-amount-${id}`}
            type='text'
            disabled={newRefund.refundable_categories[id].amount <= 0}
            onChange={(e) => updateAmount(id, e.currentTarget.value.trim())}
          />
          &nbsp;
          <span className='amount-help help-text'>
            {`(max. ${DecimalFormatter.format(newRefund.refundable_categories[id].amount)})`}
          </span>
        </label>
      </React.Fragment>
    ))}
  </React.Fragment>
);

CategorizedPartialRefund.propTypes = {
  newRefund: RefundableType.isRequired,
  updateAmount: PropTypes.func.isRequired
};

const RefundForm = (props) => {
  const {
    order,
    newRefund,
    createRefund,
    allDucksReady,
    duckMeta
  } = props;

  const DEFAULT = '_';
  const FRAUDULENT = 'fraudulent';
  const refundReasons = {
    duplicate: 'Duplicate',
    requested_by_customer: 'Customer Requested',
    fraudulent: 'Fraudulent',
    other: 'Other'
  };

  const [reason, setReason] = useState(null);
  const [partial, setPartial] = useState(false);
  const [amount, setAmount] = useState({ [DEFAULT]: newRefund.refundable_amount });
  const [amountError, setAmountError] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fraudAcknowledgement, setFraudAcknowledgement] = useState(false);

  useEffect(() => {
    // Reset `amount` entry on `partial` change
    if (partial) {
      setAmount({});
    } else {
      setAmount({ [DEFAULT]: newRefund.refundable_amount });
    }
  }, [partial, allDucksReady]);

  const amountRegex = /^\d+(?:\.\d{0,2})?$/; // matches numeric string with optional up to two decimal places

  const updateAmount = (category, categoryAmount) => {
    // destructure to remove current amount/error from state for this category
    const { [category]: prevAmount, ...otherAmounts } = amount;
    const { [category]: prevError, ...otherErrors } = amountError;

    // if amount is blank, bail early, removing category from amounts & errors
    if (categoryAmount === '') {
      setAmount(otherAmounts);
      setAmountError(otherErrors);
      return;
    }

    if (amountRegex.test(categoryAmount)) {
      const parsedAmount = parseCurrency(categoryAmount);
      const maxAmount = category === DEFAULT
        ? newRefund.refundable_amount
        : newRefund.refundable_categories[category].amount;

      if (parsedAmount > maxAmount) {
        // amount is greater than max allowed; set error
        setAmountError({ ...otherErrors, [category]: 'amount cannot be greater than the original transaction' });
      } else {
        // amount is valid; set amount and remove any error
        setAmount({ ...otherAmounts, [category]: categoryAmount });
        setAmountError(otherErrors);
      }
    } else {
      // amount is invalid; set error
      setAmountError({ ...otherErrors, [category]: 'amount is invalid' });
    }
  };

  const validateAndConfirm = () => {
    const errors = [];
    if (reason === null) {
      errors.push('Please specify a reason');
    }
    Object.keys(amountError).forEach(category => {
      if (category === DEFAULT) {
        errors.push(`Refund ${amountError[category]}`);
      } else {
        errors.push(`${newRefund.refundable_categories[category].name} ${amountError[category]}`);
      }
    });
    setValidationErrors(errors);
    if (errors.length === 0) {
      setConfirming(true);
    }
  };

  const submitRefund = () => {
    setSubmitError(null);
    setSubmitting(true);

    if (reason === FRAUDULENT && !fraudAcknowledgement) {
      setSubmitError('Please confirm that this refund is for a fraudulent payment.');
      setSubmitting(false);
      return;
    }

    const refund = {
      order_id: order.id,
      reason,
      partial,
      amount
    };

    createRefund(refund)
      .catch(error => {
        setSubmitError(error.message);
        setSubmitting(false);
      });
  };

  const SubmitButton = () => {
    if (!submitError) {
      return (
        <button
          type='submit'
          className='btn btn-primary'
          onClick={confirming ? submitRefund : validateAndConfirm}
          disabled={submitting}
        >
          {confirming ? 'Confirm' : 'Submit'}
        </button>
      )
    }
    else{
      return (<React.Fragment></React.Fragment>)
    }
  };

  const ConfirmRefund = () => (
    <div>
      {submitError && <div className='alert alert-danger'>{submitError}</div>}
      <p>
        <b>Please confirm you would like to proceed with the refund:</b>
      </p>
      <table className='table'>
        <tbody>
          <tr>
            <td><b>Amount</b></td>
            <td>{partial ? 'Partial Refund' : 'Full Refund'}</td>
            <td className='text-end refund-totals'>
              {formatCurrency(Object.values(amount).reduce((a, b) => parseCurrency(a) + parseCurrency(b), 0))}
            </td>
          </tr>
          {
            // Only display category breakdown for partial refunds with categories
            partial && newRefund.refundable_categories && Object.keys(amount).map(category => (
              <tr key={category}>
                <td />
                <td>{category === DEFAULT ? '' : newRefund.refundable_categories[category].name}</td>
                <td className='text-end'>{formatCurrency(amount[category])}</td>
              </tr>
            ))
          }
          <tr>
            <td><b>Reason</b></td>
            <td>
              {refundReasons[reason]}
            </td>
            <td />
          </tr>
        </tbody>
        <tfoot>
          {reason === FRAUDULENT && (
            <tr>
              <td />
              <td>
                <div className='checkbox'>
                  <input
                    type='checkbox'
                    checked={fraudAcknowledgement}
                    onChange={(e) => setFraudAcknowledgement(e.currentTarget.checked)}
                  />
                  <span className='text-warning'>
                    <FontAwesome name='warning' nbsp />
                    I understand that by specifying this as a fraudulent payment this
                    will block the associated payment method and email from future payments
                    through Finalsite Enrollment.
                  </span>
                </div>
              </td>
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  );

  const legacyTransaction = newRefund.legacy_transaction;
  const refundable = !legacyTransaction && Number(newRefund.refundable_amount) > 0;
  const error = duckMeta.order.error || duckMeta.newRefund.error;

  let statusMessage = null;
  if (error) {
    statusMessage = (
      <div className='alert alert-danger'>
        <FontAwesome name='info-circle' nbsp />
        {duckMeta.newRefund.errorMessage || "An unexpected error has occurred. Please try again."}
      </div>
    );
  } else if (!refundable) {
    statusMessage = (
      <div className='alert alert-info'>
        <FontAwesome name='info-circle' nbsp />
        This order has been refunded in full.
      </div>
    );
  }

  return (
    <ModalComponent title='Refund Payment' buttons={refundable || submitError ? [<SubmitButton />] : []}>
      <Placeholder.text ready={allDucksReady} width={250}>
        <React.Fragment>
          {legacyTransaction ? (
            <React.Fragment>
              <div className='alert alert-info'>
                <FontAwesome name='info-circle' nbsp />
                Please visit the Stripe Dashboard to refund this transaction.
              </div>
              <a
                className={'btn btn-primary'}
                href={newRefund.dashboard_url}
                rel={'noopener noreferrer'}
                target={'_blank'}
              >
                Go to Stripe Dashboard
                <FontAwesome icon={faExternalLink} preNbsp />
              </a>
            </React.Fragment>
          ) : statusMessage}

          {refundable && (
            <React.Fragment>
              <div className='alert alert-warning'>
                <FontAwesome name='warning' nbsp />
                Refunds take 5-10 days to appear on the customer&apos;s statement.
              </div>
              {order.state === 'refunded' && (
                <div className='alert alert-info'>
                  <FontAwesome name='info-circle' nbsp />
                  {
                    `This order has been partially refunded. The original order amount is
                     ${formatCurrency(newRefund.order_amount)}. The remaining amount that can
                     be refunded is ${formatCurrency(newRefund.refundable_amount)}.`
                  }
                </div>
              )}
              {validationErrors.length > 0 && (
                <div className='alert alert-danger'>
                  <p>Please fix the following errors before proceeding:</p>
                  <ul>
                    {validationErrors.map((e) => <li key={e}>{e}</li>)}
                  </ul>
                </div>
              )}
              {!confirming && (
                <div className='refund-form'>
                  <div className='mb-3'>
                    {/* react-select doesn't appear to satisfy this rule when nested or play nice with styles */}
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
                    <label className='col-form-label' htmlFor='refund_reason'>
                      <span>Reason</span>
                    </label>
                    <Select
                      id='refund_reason'
                      clearable
                      value={reason}
                      onChange={(opt) => setReason(opt.value)}
                      options={Object.keys(refundReasons).map((r) => ({ label: refundReasons[r], value: r }))}
                    />
                  </div>
                  <div className='mb-3'>
                    <span className='col-form-label'>Amount</span>
                    <div className='radio'>
                      <div className="form-check">
                        <label htmlFor='amount_type__full' className="form-check-label">
                          <input
                            id='amount_type__full'
                            type='radio'
                            name='amount_type'
                            checked={!partial}
                            onChange={() => setPartial(false)}
                            className="form-check-input"
                          />
                          Full
                          {!partial && (` - ${formatCurrency(newRefund.refundable_amount)}`)}
                        </label>
                      </div>
                      <div className="form-check">
                        <label htmlFor='amount_type__partial' className="form-check-label">
                          <input
                            id='amount_type__partial'
                            type='radio'
                            name='amount_type'
                            checked={partial}
                            onChange={() => setPartial(true)}
                            className="form-check-input"
                          />
                          Partial
                        </label>
                      </div>
                      {/* Refund for non-billing entities */}
                      {partial && !newRefund.refundable_categories && (
                        <PartialRefund
                          newRefund={newRefund}
                          updateAmount={(value) => updateAmount(DEFAULT, value)}
                        />
                      )}

                      {/* Refund for billing entities */}
                      {partial && newRefund.refundable_categories && (
                        <CategorizedPartialRefund
                          newRefund={newRefund}
                          updateAmount={updateAmount}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {confirming && <ConfirmRefund />}
            </React.Fragment>
          )}
        </React.Fragment>
      </Placeholder.text>
    </ModalComponent>
  );
};

RefundForm.propTypes = {
  order: OrderType,
  newRefund: RefundableType,
  createRefund: PropTypes.func,
  allDucksReady: PropTypes.bool,
  duckMeta: PropTypes.shape({
    order: PropTypes.shape({ error: PropTypes.bool }),
    newRefund: PropTypes.shape({ error: PropTypes.bool })
  })
};

RefundForm.defaultProps = {
  order: null,
  newRefund: { refundable_amount: 0 },
  createRefund: () => ({}),
  allDucksReady: false,
  duckMeta: {
    order: { error: false },
    newRefund: { error: false }
  }
};

const mapDucksToProps = (props) => {
  const { orderId } = props;

  const order = singleDuck(orders, orderId);

  return {
    order,
    newRefund: dependentDuck(order, ({ value }) => refunds.newRefund(value.id))
  };
};
const mapStateToProps = () => { };
const mapDispatchToProps = (dispatch) => ({
  createRefund: (refund) => dispatch(refunds.actions.create(refund))
    .then(refunds.actions.denormalizeObjects) // TODO Refund v. OrderTransaction
    .then(refundTransaction => {
      if (refundTransaction.success) {
        return window.location.reload();
      }

      return Promise.reject(new Error(refundTransaction.message));
    })
});

export default registeredComponent('RefundForm')(
  duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(RefundForm)
);
